import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Search,
  Filter,
  RefreshCw,
  MessageSquare,
  Clock,
  Calendar,
  Hash,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertTriangle,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useTickets } from '../../contexts/TicketContext';
import { useDynamicGradient } from '../../hooks/useDynamicGradient';

/**
 * Componente de listagem de tickets modernizado
 * Com indicadores visuais e atualização em tempo real
 */
const TicketList = () => {
  const { user } = useAuth();
  const { tickets, loading } = useTickets();
  const navigate = useNavigate();
  const dynamicGradient = useDynamicGradient();

  // Estados para filtros e busca
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('lastUpdate');
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Categorias disponíveis (Sincronizadas com TicketContext)
  const categories = [
    { id: 'suporte_duvidas', label: 'Dúvidas Gerais' },
    { id: 'compras_doacoes', label: 'Compras & Doações' },
    { id: 'cupons_descontos', label: 'Cupons & Descontos' },
    { id: 'denuncia_jogador', label: 'Denúncia Jogador' },
    { id: 'contestar_punicao', label: 'Revogar Punição' },
    { id: 'programacao', label: 'Problemas Técnicos' },
    { id: 'denuncia_staff', label: 'Denúncia Staff' }
  ];

  // Função para obter label da categoria
  const getCategoryLabel = (categoryId) => {
    if (categoryId === 'ticket_automatico') return 'Automático';
    const categories = [
      { id: 'suporte_duvidas', label: 'Dúvidas Gerais' },
      { id: 'compras_doacoes', label: 'Compras & Doações' },
      { id: 'cupons_descontos', label: 'Cupons & Descontos' },
      { id: 'denuncia_jogador', label: 'Denúncia Jogador' },
      { id: 'contestar_punicao', label: 'Revogar Punição' },
      { id: 'programacao', label: 'Problemas Técnicos' },
      { id: 'denuncia_staff', label: 'Denúncia Staff' }
    ];
    const category = categories.find(c => c.id === categoryId);
    return category ? category.label : categoryId;
  };

  // Auto-refresh logic removed (handled by Firestore)

  // Filtrar e ordenar tickets
  useEffect(() => {
    if (!tickets || !user) return;

    let filtered = tickets.filter(ticket => {
      const isOwner = String(ticket.userId) === String(user.id) ||
        String(ticket.userId) === String(user.discordId) ||
        String(ticket.userDiscordId) === String(user.discordId);

      const isInternal = ticket.type === 'escalation' || ticket.category === 'escalacao_interna';

      return isOwner && !isInternal;
    });

    // Aplica filtro de busca
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(ticket =>
        ticket.title.toLowerCase().includes(term) ||
        ticket.description.toLowerCase().includes(term) ||
        ticket.id.toString().includes(term)
      );
    }

    // Aplica filtro de status (Dual-Layer)
    if (statusFilter !== 'all') {
      filtered = filtered.filter(ticket => {
        // Verifica status estrutural
        if (ticket.status === statusFilter) return true;
        // Verifica status auxiliar se for um array
        if (Array.isArray(ticket.auxiliaryStatus) && ticket.auxiliaryStatus.includes(statusFilter)) return true;
        return false;
      });
    }

    // Aplica filtro de categoria
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.category === categoryFilter);
    }

    // Aplica ordenação
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'lastUpdate':
      case 'updated':
        filtered.sort((a, b) => new Date(b.lastUpdate) - new Date(a.lastUpdate));
        break;
      case 'priority':
        const priorityOrder = { 'alta': 3, 'media': 2, 'baixa': 1 };
        filtered.sort((a, b) => (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0));
        break;
      default:
        // Padrão: última atualização
        filtered.sort((a, b) => new Date(b.lastUpdate) - new Date(a.lastUpdate));
        break;
    }

    setFilteredTickets(filtered);
  }, [tickets, searchTerm, statusFilter, categoryFilter, sortBy, user]);

  // Estatísticas dos tickets (Dual-Layer)
  const getTicketStats = () => {
    const userTickets = tickets.filter(t => t.userId === user?.id);
    return {
      total: userTickets.length,
      // Abertos: Structural 'aberto' AND NOT 'fechado' OR 'aguardando_sistema'
      open: userTickets.filter(t => t.status === 'aberto' || t.status === 'aguardando_sistema').length,
      // Em andamento: Has auxiliary 'em_andamento'
      inProgress: userTickets.filter(t => Array.isArray(t.auxiliaryStatus) && t.auxiliaryStatus.includes('em_andamento')).length,
      // Aguardando Staff: Has auxiliary 'aguardando_staff'
      waitingStaff: userTickets.filter(t => Array.isArray(t.auxiliaryStatus) && t.auxiliaryStatus.includes('aguardando_staff')).length,
      // Resolvido: Has auxiliary 'resolvido'
      resolved: userTickets.filter(t => Array.isArray(t.auxiliaryStatus) && t.auxiliaryStatus.includes('resolvido')).length,
      // Fechado: Structural 'fechado'
      closed: userTickets.filter(t => t.status === 'fechado').length
    };
  };

  // Funções de navegação
  const handleCreateTicket = () => navigate('/tickets/create');
  const handleViewTicket = (ticketId) => {
    navigate(`/tickets/${ticketId}`);
  };

  // Função para atualização manual
  const handleRefresh = () => {
    setLastUpdate(new Date());
  };

  // Função para formatar tempo relativo
  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Data desconhecida';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';

    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Agora';
    if (diffInMinutes < 60) return `${diffInMinutes}min atrás`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h atrás`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d atrás`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}sem atrás`;
  };

  // Função para obter cor do status
  const getStatusBadgeColor = (status) => {
    const colors = {
      // Structural
      'aberto': 'bg-green-500/20 text-green-400 border-green-500/30',
      'aguardando_sistema': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'fechado': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',

      // Auxiliary
      'aguardando_usuario': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'aguardando_staff': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'em_andamento': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
      'escalado': 'bg-red-500/20 text-red-400 border-red-500/30',
      'resolvido': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'controle_qualidade': 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  // Função para obter label amigável do status
  const getStatusLabel = (status) => {
    const labels = {
      'aberto': 'Aberto',
      'aguardando_sistema': 'Verificando',
      'fechado': 'Resolvido',
      'aguardando_usuario': 'Aguard. Você',
      'aguardando_staff': 'Aguard. Staff',
      'em_andamento': 'Em Andamento',
      'escalado': 'Escalado',
      'resolvido': 'Resolvido',
      'controle_qualidade': 'C. Qualidade'
    };
    return labels[status] || status.replace(/_/g, ' ');
  };

  // Ícones para as estatísticas
  const statIcons = {
    total: <Hash className="h-5 w-5 text-blue-400" />,
    open: <Clock className="h-5 w-5 text-yellow-400" />,
    processing: <RefreshCw className="h-5 w-5 text-purple-400" />,
    resolved: <CheckCircle className="h-5 w-5 text-green-400" />
  };

  // Função para obter cor da prioridade
  const getPriorityBadgeColor = (priority) => {
    const colors = {
      'alta': 'bg-red-500/20 text-red-400 border-red-500/30',
      'media': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'baixa': 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    return colors[priority] || colors['media'];
  };

  // Componente de card de ticket modernizado e informativo
  const TicketCard = ({ ticket }) => {
    // Verifica se o ticket não foi visualizado
    const isUnread = !ticket.lastViewed || new Date(ticket.lastUpdate) > new Date(ticket.lastViewed);

    // Obtém a última mensagem para exibição
    const lastResponse = ticket.responses && ticket.responses.length > 0
      ? ticket.responses[ticket.responses.length - 1]
      : null;

    const lastMessagePreview = lastResponse
      ? lastResponse.content
      : ticket.description;

    return (
      <motion.div
        whileHover={{ scale: 1.01, y: -2 }}
        className={`group relative overflow-hidden rounded-xl md:rounded-2xl border transition-all duration-300 cursor-pointer active:scale-[0.98] ${isUnread
          ? 'bg-gradient-to-br from-blue-500/10 to-purple-500/5 border-blue-500/30'
          : 'bg-white/[0.03] border-white/10 hover:border-white/20 hover:bg-white/[0.08] backdrop-blur-xl'
          }`}
        onClick={() => handleViewTicket(ticket.id)}
      >
        {/* Efeito de brilho no hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/10 to-purple-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 backdrop-blur-3xl" />

        <div className="relative p-4 md:p-5">
          {/* Topo: ID, Categoria e Status */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center justify-between gap-2 sm:gap-3 mb-3 md:mb-4">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="flex flex-col">
                <span className="text-[9px] md:text-[10px] text-gray-500 uppercase font-black tracking-widest mb-0.5">Ticket ID</span>
                <span className="text-white font-mono text-xs md:text-sm font-bold">#{ticket.id}</span>
              </div>
              <div className="h-6 md:h-8 w-[1px] bg-white/10 mx-1" />
              <Badge className="bg-blue-600/20 text-blue-400 border-none px-2 md:px-2.5 py-0.5 md:py-1 text-[9px] md:text-[10px] font-black uppercase tracking-wider rounded-md truncate max-w-[120px] sm:max-w-none">
                {getCategoryLabel(ticket.category)}
              </Badge>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Prioridade */}
              <div className={`flex items-center space-x-1 md:space-x-1.5 px-1.5 md:px-2 py-0.5 md:py-1 rounded-md border ${getPriorityBadgeColor(ticket.priority)}`}>
                <div className={`w-1 h-1 md:w-1.5 md:h-1.5 rounded-full animate-pulse ${ticket.priority === 'alta' ? 'bg-red-400' : ticket.priority === 'media' ? 'bg-yellow-400' : 'bg-green-400'}`} />
                <span className="text-[9px] md:text-[10px] font-bold uppercase tracking-tight">{ticket.priority || 'Média'}</span>
              </div>

              <Badge className={`text-[9px] md:text-[10px] px-2 md:px-3 py-0.5 md:py-1 font-bold uppercase tracking-wider ${getStatusBadgeColor(ticket.status)} border-none rounded-full shadow-lg shadow-black/20`}>
                {getStatusLabel(ticket.status)}
              </Badge>
            </div>
          </div>

          {/* Conteúdo: Título e Preview */}
          <div className="mb-3 md:mb-4">
            <h3 className="text-base md:text-lg font-bold text-white group-hover:text-blue-300 transition-colors duration-300 line-clamp-1 mb-1">
              {ticket.title || 'Sem título'}
            </h3>
            <p className="text-xs md:text-sm text-gray-400 line-clamp-2 leading-relaxed opacity-80 group-hover:opacity-100 transition-opacity">
              {lastMessagePreview}
            </p>
          </div>

          {/* Footer: Datas e Atividade */}
          <div className="flex items-center justify-between pt-3 md:pt-4 border-t border-white/5">
            <div className="flex items-center">
              <div className="flex items-center text-gray-500 text-[10px] md:text-[11px]">
                <Clock className="h-3 w-3 mr-1 md:mr-1.5 opacity-50" />
                <span className="hidden sm:inline">Atualizado </span>
                <span>{formatRelativeTime(ticket.updated_at || ticket.updatedAt || ticket.last_update || ticket.lastUpdate || ticket.created_at || ticket.createdAt)}</span>
              </div>
            </div>

            <div className="flex items-center text-gray-400 group-hover:text-white transition-colors">
              <span className="text-[10px] md:text-[11px] font-bold mr-1.5 md:mr-2 uppercase tracking-widest hidden sm:inline">Abrir</span>
              <Eye className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>

        {/* Marcador de não lido (Glow lateral) */}
        {isUnread && (
          <>
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-purple-600" />
            <div className="absolute left-0 top-0 bottom-0 w-4 bg-blue-500/10 blur-md" />
          </>
        )}
      </motion.div>
    );
  };
  const stats = getTicketStats();

  return (
    <div className="w-full min-h-full bg-gradient-to-br from-black via-gray-900 to-blue-900 animate-gradient-slow text-white p-6 rounded-3xl space-y-8 pb-10">
      {/* Header Moderno */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative"
      >
        <div className="flex flex-col gap-4 md:gap-6">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter">
              <span className={`bg-gradient-to-r ${dynamicGradient} bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-x`}>
                CENTRAL DE ATENDIMENTO
              </span>
            </h1>
            <p className="text-sm md:text-base lg:text-lg text-gray-400 font-medium max-w-2xl">
              Gerencie seus chamados e acompanhe o progresso das suas solicitações em tempo real.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <div className="hidden lg:flex flex-col items-end px-4 py-2 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-sm">
              <span className="text-[10px] text-gray-500 uppercase font-black tracking-widest">Sincronizado</span>
              <span className="text-white font-mono text-sm font-bold flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                {lastUpdate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <Button
              onClick={handleCreateTicket}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-black uppercase text-xs tracking-wider h-12 md:h-14 px-6 md:px-8 rounded-2xl shadow-xl shadow-blue-900/20 group transition-all justify-center"
            >
              <Plus className="h-5 w-5 mr-2 group-hover:rotate-90 transition-transform" />
              Novo Chamado
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Grid de Estatísticas QuickView */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {[
          { label: 'Total de Chamados', value: stats.total, color: 'from-blue-600/20', icon: statIcons.total },
          { label: 'Aguardando Staff', value: stats.waitingStaff, color: 'from-yellow-600/20', icon: statIcons.open },
          { label: 'Aguardando Você', value: Math.max(0, stats.open - stats.waitingStaff), color: 'from-purple-600/20', icon: statIcons.processing },
          { label: 'Finalizados', value: stats.resolved + stats.closed, color: 'from-green-600/20', icon: statIcons.resolved },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 + 0.3 }}
            className={`relative overflow-hidden p-4 md:p-6 rounded-2xl md:rounded-3xl bg-gradient-to-br ${item.color} to-transparent border border-white/10 backdrop-blur-md group hover:border-white/20 transition-all`}
          >
            <div className="absolute top-0 right-0 p-2 md:p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              {React.cloneElement(item.icon, { className: "w-10 h-10 md:w-16 md:h-16" })}
            </div>
            <div className="relative z-10 flex flex-col gap-1">
              <div className="flex items-center gap-1 md:gap-2 mb-1">
                {React.cloneElement(item.icon, { className: "w-4 h-4 md:w-5 md:h-5" })}
                <span className="text-[8px] md:text-[10px] text-gray-400 font-black uppercase tracking-widest line-clamp-1">{item.label}</span>
              </div>
              <span className="text-2xl md:text-4xl font-black text-white leading-tight">{item.value || 0}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Barra de Ferramentas (Busca e Filtros) */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.7 }}
        className="flex flex-col gap-3 md:gap-4 p-3 md:p-4 bg-white/[0.02] border border-white/10 rounded-2xl md:rounded-[2.5rem] backdrop-blur-[40px] shadow-2xl"
      >
        <div className="relative group w-full">
          <Search className="absolute left-4 md:left-5 top-1/2 -translate-y-1/2 h-4 w-4 md:h-5 md:w-5 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
          <Input
            placeholder="Pesquisar por ID, título ou descrição..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-12 md:h-14 bg-black/40 border-white/5 rounded-xl md:rounded-2xl pl-11 md:pl-14 pr-4 md:pr-6 focus:ring-2 focus:ring-blue-500/50 transition-all text-white placeholder:text-gray-600 text-sm md:text-base"
          />
        </div>

        <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-11 md:h-14 w-full md:w-[180px] bg-black/40 border-white/5 rounded-xl md:rounded-2xl text-gray-400 hover:text-white font-bold uppercase text-[9px] md:text-[10px] tracking-widest focus:ring-2 focus:ring-blue-500/50 transition-colors">
              <Filter className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 opacity-50" />
              <SelectValue placeholder="STATUS" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10 text-white rounded-2xl">
              <SelectItem value="all">TODOS STATUS</SelectItem>
              <SelectItem value="aberto">ABERTOS</SelectItem>
              <SelectItem value="em_andamento">EM ANDAMENTO</SelectItem>
              <SelectItem value="aguardando_staff">AGUARD. STAFF</SelectItem>
              <SelectItem value="resolvido">RESOLVIDOS</SelectItem>
              <SelectItem value="fechado">FECHADOS</SelectItem>
            </SelectContent>
          </Select>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="h-11 md:h-14 w-full md:w-[180px] bg-black/40 border-white/5 rounded-xl md:rounded-2xl text-gray-400 hover:text-white font-bold uppercase text-[9px] md:text-[10px] tracking-widest focus:ring-2 focus:ring-blue-500/50 transition-colors">
              <Hash className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 opacity-50" />
              <SelectValue placeholder="CATEGORIA" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10 text-white rounded-2xl">
              <SelectItem value="all">CATEGORIAS</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.label.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-11 md:h-14 w-full md:w-[180px] bg-black/40 border-white/5 rounded-xl md:rounded-2xl text-gray-400 hover:text-white font-bold uppercase text-[9px] md:text-[10px] tracking-widest focus:ring-2 focus:ring-blue-500/50 transition-colors">
              <div className="flex items-center gap-1 md:gap-2">
                {sortBy === 'priority' ? <TrendingUp className="w-3 h-3 md:w-4 md:h-4" /> : <Calendar className="w-3 h-3 md:w-4 md:h-4" />}
                <SelectValue placeholder="ORDENAR" />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10 text-white rounded-2xl">
              <SelectItem value="lastUpdate">ÚLTIMA ATUALIZAÇÃO</SelectItem>
              <SelectItem value="newest">MAIS RECENTES</SelectItem>
              <SelectItem value="oldest">MAIS ANTIGOS</SelectItem>
              <SelectItem value="priority">MAIOR PRIORIDADE</SelectItem>
            </SelectContent>
          </Select>

          <Button
            onClick={handleRefresh}
            variant="outline"
            className="h-11 md:h-14 w-full md:w-14 rounded-xl md:rounded-2xl bg-black/40 border-white/5 text-gray-500 hover:text-white hover:bg-white/10 group transition-all"
          >
            <RefreshCw className="h-4 w-4 md:h-5 md:w-5 group-active:rotate-180 transition-transform duration-500" />
            <span className="ml-2 md:hidden">Atualizar</span>
          </Button>
        </div>
      </motion.div>

      {/* Lista de Chamados */}
      {(loading && filteredTickets.length === 0) ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-[200px] bg-white/5 border border-white/10 rounded-3xl animate-pulse" />
          ))}
        </div>
      ) : filteredTickets.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {filteredTickets.map((ticket) => (
            <div key={ticket.id}>
              <TicketCard ticket={ticket} />
            </div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1 }}
          className="flex flex-col items-center justify-center py-20 px-6 text-center bg-black/20 border border-white/5 rounded-[3rem] backdrop-blur-sm"
        >
          <div className="mb-8 relative">
            <div className="absolute inset-0 bg-blue-500/20 blur-3xl rounded-full" />
            <div className="relative p-10 bg-gradient-to-br from-slate-800 to-slate-900 rounded-[2.5rem] border border-white/10 shadow-2xl">
              <MessageSquare className="h-16 w-16 text-blue-500 opacity-80" />
            </div>
          </div>
          <h3 className="text-3xl font-black text-white mb-3">Nenhum Chamado Encontrado</h3>
          <p className="text-gray-400 text-lg max-w-md mx-auto mb-10 font-medium">
            {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
              ? 'Não encontramos nenhum ticket com os filtros aplicados.'
              : 'Parece que você ainda não criou nenhum ticket. Comece agora para receber suporte da nossa equipe.'
            }
          </p>
          <Button
            onClick={handleCreateTicket}
            className="bg-white text-black hover:bg-gray-200 font-black uppercase text-sm tracking-wider h-14 px-10 rounded-2xl shadow-xl shadow-white/5 group"
          >
            <Plus className="h-5 w-5 mr-3 group-hover:rotate-90 transition-transform" />
            Criar Primeiro Chamado
          </Button>
        </motion.div>
      )}
    </div>
  );
};

export default TicketList;
